import {filter, find, includes, path, propEq} from 'ramda';
import { createSelector } from 'reselect';
import { selectRailways } from "../../modules/selectors/railway";

export const selectRides = state => path(['api', 'ride', 'data'], state) || [];

export const selectActiveRides = createSelector(
  [selectRides],
  (rides) => {
    if (!rides || rides.length === 0) return [];

    const isActive = ride => typeof path(['attributes', 'distance-depot-end'], ride) !== 'number';

    return filter(isActive)(rides);
  },
);

export const selectInactiveRides = createSelector(
  [selectRides],
  (rides) => {
    if (!rides || rides.length === 0) return [];

    const isInactive = ride => typeof path(['attributes', 'distance-depot-end'], ride) === 'number';

    return filter(isInactive)(rides);
  },
);

export const selectApiUsers = state => path(['api', 'api-user', 'data'], state) || [];
export const selectDriverIdFromRide = (state, ownProps) => ownProps.ride.relationships.driver.data.id;
export const getDriverFromRide = createSelector(
  [selectApiUsers, selectDriverIdFromRide],
  (apiUsers, driverId) => find(propEq('id', driverId))(apiUsers),
);
export const makeGetDriverFromRide = () => createSelector(
  [selectApiUsers, selectDriverIdFromRide],
  (apiUsers, driverId) => find(propEq('id', driverId))(apiUsers),
);

export const selectVehicles = state => path(['api', 'vehicle', 'data'], state) || [];
export const selectVehicleIdFromRide = (state, ownProps) => ownProps.ride.relationships.vehicle.data.id;
export const getVehicleFromRide = createSelector(
  [selectVehicles, selectVehicleIdFromRide],
  (vehicles, vehicleId) => find(propEq('id', vehicleId))(vehicles),
);
export const makeGetVehicleFromRide = () => createSelector(
  [selectVehicles, selectVehicleIdFromRide],
  (vehicles, vehicleId) => find(propEq('id', vehicleId))(vehicles),
);

export const selectCompanies = state => path(['api', 'company', 'data'], state) || [];
export const selectCompanyIdFromRide = (state, ownProps) => String(ownProps.ride.attributes['company-id']);
export const getCompanyFromRide = createSelector(
  [selectCompanies, selectCompanyIdFromRide],
  (companies, companyId) => find(propEq('id', companyId))(companies),
);
export const makeGetCompanyFromRide = () => createSelector(
  [selectCompanies, selectCompanyIdFromRide],
  (companies, companyId) => find(propEq('id', companyId))(companies),
);

export const selectRouteBlockages = state => path(['api', 'route-blockage', 'data'], state) || [];
export const selectRouteBlockageIdFromRide = (state, ownProps) => ownProps.ride.relationships.routeblockage.data.id;
export const getRouteBlockageFromRide = createSelector(
  [selectRouteBlockages, selectRouteBlockageIdFromRide],
  (routeBlockages, routeBlockageId) => find(propEq('id', routeBlockageId))(routeBlockages),
);
export const makeGetRouteBlockageFromRide = () => createSelector(
  [selectRouteBlockages, selectRouteBlockageIdFromRide],
  (routeBlockages, routeBlockageId) => find(propEq('id', routeBlockageId))(routeBlockages),
);

export const selectStops = state => path(['api', 'stop', 'data'], state) || [];
export const makeGetRouteBlockageNameFromRide = () => createSelector(
  [selectRouteBlockages, selectStops, selectRouteBlockageIdFromRide],
  (routeBlockages, stops, routeBlockageId) => {
    const routeBlockage = find(propEq('id', routeBlockageId))(routeBlockages);

    if (!routeBlockage) return '--';

    const stopFromId = routeBlockage.relationships['stop-from'].data.id;
    const stopToId = routeBlockage.relationships['stop-to'].data.id;

    const stopFrom = find(propEq('id', stopFromId))(stops);
    const stopTo = find(propEq('id', stopToId))(stops);

    const stopFromName = stopFrom ? stopFrom.attributes.name : 'Onbekend';
    const stopToName = stopTo ? stopTo.attributes.name : 'Onbekend';

    return `${stopFromName} naar ${stopToName}`;
  },
);

export const selectRideAdminOptions = state => path(['rideAdmin', 'options'], state);

export const makeGetRelatedStopsFromRouteBlockage = ()  => createSelector(
  [ getRouteBlockageFromRide, selectRailways, selectStops ],
  ( routeBlockage, railways, stops) => {
    const railwayId = path(['relationships', 'railway', 'data', 'id'], routeBlockage);
    if (!railwayId) return [];

    const railway = find(propEq('id', railwayId))(railways);
    if (!railway) return [];

    const relatedStops = path(['relationships', 'stops', 'data'], railway);
    if (!relatedStops) return [];

    const relatedStopIds = relatedStops.map(stop => stop.id);

    return filter(stop => includes(stop.id, relatedStopIds), stops)
      .map(relatedStop => ({
        id: relatedStop.id,
        name: relatedStop.attributes.name,
      }));
  },
);
